import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, X, Loader, Eye, EyeOff, Camera, Image as ImageIcon, CheckCircle, Globe, Filter } from 'lucide-react';
import api from '../../utils/api';
import { resizeImage } from '../../utils/imageUtils';

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";
const CATS = ['fleet','machinery','operations','team','before-after'];

export default function Gallery() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [showAdd, setShowAdd]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected]   = useState(new Set()); // for bulk actions
  const [toast, setToast]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({ url:'', title:'', category:'fleet', published:true });
  const [pendingPhotos, setPendingPhotos] = useState([]); // photos staged before save
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/gallery'); setItems(r.data); }
    catch(e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = catFilter === 'all' ? items : items.filter(i => i.category === catFilter);

  // ── File upload handler ──────────────────────────────────────────────────
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const compressed = await Promise.all(files.map(f => resizeImage(f)));
      setPendingPhotos(prev => [...prev, ...compressed]);
      if (!showAdd) setShowAdd(true);
    } catch { alert('Upload failed — try again.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const saveAll = async () => {
    if (!pendingPhotos.length && !form.url) return;
    setSaving(true);
    const toSave = pendingPhotos.length > 0
      ? pendingPhotos.map(url => ({ url, title: form.title || 'Apex Towing', category: form.category, published: form.published }))
      : [{ url: form.url, title: form.title, category: form.category, published: form.published }];
    try {
      await Promise.all(toSave.map(item => api.post('/api/gallery', item)));
      showToast(`✅ ${toSave.length} photo${toSave.length>1?'s':''} added to gallery!`);
      setShowAdd(false);
      setForm({ url:'', title:'', category:'fleet', published:true });
      setPendingPhotos([]);
      load();
    } catch { alert('Failed to save photos.'); }
    finally { setSaving(false); }
  };

  const toggle = async (id, published) => {
    await api.put(`/api/gallery/${id}`, { published: !published });
    showToast(published ? 'Unpublished' : '✅ Published to website!');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await api.delete(`/api/gallery/${id}`);
    setSelected(s => { const n = new Set(s); n.delete(id); return n; });
    showToast('Deleted');
    load();
  };

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const bulkPublish = async (pub) => {
    await Promise.all([...selected].map(id => api.put(`/api/gallery/${id}`, { published: pub })));
    showToast(pub ? `✅ ${selected.size} photos published!` : `${selected.size} photos unpublished`);
    setSelected(new Set());
    load();
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} photo${selected.size>1?'s':''}?`)) return;
    await Promise.all([...selected].map(id => api.delete(`/api/gallery/${id}`)));
    showToast(`${selected.size} photos deleted`);
    setSelected(new Set());
    load();
  };

  const pubCount   = items.filter(i => i.published).length;
  const draftCount = items.filter(i => !i.published).length;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*"                       multiple className="hidden" onChange={handleFile} />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Gallery</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            <span className="text-green-600 font-bold">{pubCount} live</span> on website ·
            <span className="text-gray-400 ml-1">{draftCount} draft</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => cameraRef.current?.click()}
            className="flex items-center gap-2 border-2 border-apex-blue-200 text-apex-blue-700 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-50">
            <Camera className="w-4 h-4" /> Camera
          </button>
          <button onClick={() => galleryRef.current?.click()}
            className="flex items-center gap-2 border-2 border-gray-200 text-gray-600 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-50">
            <ImageIcon className="w-4 h-4" /> Upload
          </button>
          <button onClick={() => { setForm({ url:'', title:'', category:'fleet', published:true }); setPendingPhotos([]); setShowAdd(true); }}
            className="flex items-center gap-2 bg-apex-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-600">
            <Plus className="w-4 h-4" /> Add by URL
          </button>
        </div>
      </div>

      {/* Uploading indicator */}
      {uploading && (
        <div className="bg-apex-blue-50 border-2 border-apex-blue-200 rounded-xl p-4 mb-4 flex items-center gap-3">
          <Loader className="w-5 h-5 text-apex-blue-600 animate-spin" />
          <p className="text-apex-blue-700 font-semibold text-sm">Compressing and uploading photos...</p>
        </div>
      )}

      {/* Category filter + bulk bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {[['all','All'], ...CATS.map(c=>[c,c.charAt(0).toUpperCase()+c.slice(1)])].map(([id,label]) => (
            <button key={id} onClick={() => setCatFilter(id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all
                ${catFilter===id ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {label} {id!=='all' && <span className="opacity-60">({items.filter(i=>i.category===id).length})</span>}
            </button>
          ))}
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-apex-blue-50 border border-apex-blue-200 rounded-xl px-3 py-2 ml-auto">
            <span className="text-xs font-bold text-apex-blue-700">{selected.size} selected</span>
            <button onClick={() => bulkPublish(true)} className="text-xs font-bold bg-green-600 text-white px-2.5 py-1 rounded-lg hover:bg-green-700">Publish All</button>
            <button onClick={() => bulkPublish(false)} className="text-xs font-bold bg-gray-500 text-white px-2.5 py-1 rounded-lg hover:bg-gray-600">Unpublish</button>
            <button onClick={bulkDelete} className="text-xs font-bold bg-red-500 text-white px-2.5 py-1 rounded-lg hover:bg-red-600">Delete</button>
            <button onClick={() => setSelected(new Set())} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* Gallery grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-600">No photos in this category yet</p>
          <p className="text-sm mt-1">Use the Camera or Upload buttons to add photos</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map(item => {
            const isSel = selected.has(item.id);
            return (
              <div key={item.id} className={`bg-white rounded-2xl border-2 overflow-hidden transition-all hover:shadow-md
                ${isSel ? 'border-apex-blue-500 shadow-md' : 'border-gray-200'}`}>
                <div className="relative aspect-[4/3] bg-gray-100">
                  <img src={item.url} alt={item.title} className="w-full h-full object-cover"
                    onError={e => { e.target.style.display='none'; }} />

                  {/* Status badge */}
                  <div className={`absolute top-2 left-2 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1
                    ${item.published ? 'bg-green-500 text-white' : 'bg-gray-600/80 text-white'}`}>
                    {item.published ? <><Globe className="w-2.5 h-2.5" /> Live</> : 'Draft'}
                  </div>

                  {/* Select checkbox */}
                  <button onClick={() => toggleSelect(item.id)}
                    className={`absolute top-2 right-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all
                      ${isSel ? 'bg-apex-blue-600 border-apex-blue-600' : 'bg-white/80 border-gray-300 hover:border-apex-blue-400'}`}>
                    {isSel && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                  </button>
                </div>

                <div className="p-3">
                  <p className="font-semibold text-gray-800 text-sm truncate">{item.title}</p>
                  <p className="text-xs text-gray-400 capitalize mt-0.5">{item.category}</p>
                  <div className="flex gap-1.5 mt-2">
                    <button onClick={() => toggle(item.id, item.published)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold border-2 transition-all flex items-center justify-center gap-1
                        ${item.published
                          ? 'border-gray-200 text-gray-500 hover:bg-gray-50'
                          : 'border-green-200 text-green-600 hover:bg-green-50 bg-green-50'}`}>
                      {item.published ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Publish</>}
                    </button>
                    <button onClick={() => del(item.id)}
                      className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add / Upload Modal ── */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) { setShowAdd(false); setPendingPhotos([]); } }}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="font-black text-apex-blue-700">
                {pendingPhotos.length > 0 ? `Add ${pendingPhotos.length} Photo${pendingPhotos.length>1?'s':''}` : 'Add Photo by URL'}
              </h2>
              <button onClick={() => { setShowAdd(false); setPendingPhotos([]); }}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-4">

              {/* Pending photos preview */}
              {pendingPhotos.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">{pendingPhotos.length} Photos Ready to Save</p>
                  <div className="grid grid-cols-3 gap-2">
                    {pendingPhotos.map((src,i) => (
                      <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPendingPhotos(p=>p.filter((_,idx)=>idx!==i))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* Add more */}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => cameraRef.current?.click()}
                      className="flex-1 py-2 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 flex items-center justify-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" /> Add More
                    </button>
                    <button onClick={() => galleryRef.current?.click()}
                      className="flex-1 py-2 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 flex items-center justify-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> More Files
                    </button>
                  </div>
                </div>
              )}

              {/* URL input (when no camera photos) */}
              {pendingPhotos.length === 0 && (
                <>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-bold text-gray-500 uppercase">Image URL *</span>
                    <input value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} className={inputCls} placeholder="https://..." />
                  </label>
                  {form.url && <img src={form.url} alt="" className="w-full rounded-xl h-40 object-cover" onError={e=>e.target.style.display='none'} />}
                  <p className="text-center text-xs text-gray-400">— or —</p>
                  <div className="flex gap-2">
                    <button onClick={() => cameraRef.current?.click()}
                      className="flex-1 py-3 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 flex items-center justify-center gap-1.5">
                      <Camera className="w-4 h-4" /> Take Photo
                    </button>
                    <button onClick={() => galleryRef.current?.click()}
                      className="flex-1 py-3 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-apex-blue-200 flex items-center justify-center gap-1.5">
                      <ImageIcon className="w-4 h-4" /> Choose Files
                    </button>
                  </div>
                </>
              )}

              {/* Title, category, publish */}
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase">Title / Caption</span>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} className={inputCls} placeholder="Our tow truck in action" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase">Category</span>
                <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className={`${inputCls} bg-white`}>
                  {CATS.map(c=><option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-3 cursor-pointer bg-gray-50 rounded-xl p-3">
                <input type="checkbox" checked={form.published} onChange={e=>setForm(f=>({...f,published:e.target.checked}))} className="w-4 h-4 accent-apex-blue-700" />
                <div>
                  <p className="text-sm font-semibold text-gray-700">Publish immediately</p>
                  <p className="text-xs text-gray-400">Makes photo visible on your website now</p>
                </div>
              </label>
              <button onClick={saveAll} disabled={(!pendingPhotos.length && !form.url) || saving}
                className="w-full bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving
                  ? <Loader className="w-4 h-4 animate-spin" />
                  : pendingPhotos.length > 0
                    ? <><CheckCircle className="w-4 h-4" /> Save {pendingPhotos.length} Photo{pendingPhotos.length>1?'s':''}</>
                    : <><Plus className="w-4 h-4" /> Add Photo</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}